import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/joy';
import type { PlatformBoardStatus } from '../../../types/admin';

/** Board position, not an inferred history of lifecycle steps completed. */
export function CrmBoardStageBar({ columns, currentId, disabled, onMove }: {
  columns: PlatformBoardStatus[];
  currentId: number | null;
  disabled: boolean;
  onMove: (column: PlatformBoardStatus) => void;
}) {
  const active = useRef<HTMLButtonElement>(null);
  useEffect(() => { active.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }, [currentId]);
  return <Box sx={{ minWidth: 0 }}>
    <Typography level="body-xs" sx={{ mb: 1, color: 'text.secondary' }}>Board stage · Select a stage to move this prospect</Typography>
    <Box role="group" aria-label="Board stages" sx={{ display: 'flex', overflowX: 'auto', gap: 1, pb: 1, scrollSnapType: 'x proximity' }}>
      {columns.map((column, index) => <Button
        key={column.id}
        ref={column.id === currentId ? active : undefined}
        size="sm"
        color={column.id === currentId ? 'primary' : 'neutral'}
        variant={column.id === currentId ? 'soft' : 'plain'}
        aria-label={`Move to ${column.name}`}
        aria-current={column.id === currentId ? 'step' : undefined}
        disabled={disabled || column.id === currentId}
        onClick={() => onMove(column)}
        sx={{
          flexShrink: 0,
          maxWidth: 260,
          scrollSnapAlign: 'start',
          overflowWrap: 'anywhere',
          ...(column.id === currentId ? {
            '&.Mui-disabled': { bgcolor: 'primary.softBg', color: 'primary.softColor', opacity: 1 },
          } : {}),
        }}
      >{index + 1}. {column.name}</Button>)}
    </Box>
    {currentId === null && <Typography level="body-xs">Not yet placed on this board.</Typography>}
  </Box>;
}
